import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  usePlatformSettings,
  useUpdatePlatformSettings,
  type UpdatePlatformSettingsInput,
} from "@/hooks/usePlatformSettings";

export const SupportContactSection = () => {
  const { data, isLoading } = usePlatformSettings();
  const update = useUpdatePlatformSettings();

  const [form, setForm] = useState({
    supportEmail: "",
    supportPhone: "",
    supportWhatsapp: "",
    legalEmail: "",
    officeAddress: "",
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      supportEmail: data.supportEmail ?? "",
      supportPhone: data.supportPhone ?? "",
      supportWhatsapp: data.supportWhatsapp ?? "",
      legalEmail: data.legalEmail ?? "",
      officeAddress: data.officeAddress ?? "",
    });
  }, [data]);

  const onSave = () => {
    const payload: UpdatePlatformSettingsInput = {
      supportEmail: form.supportEmail.trim() || null,
      supportPhone: form.supportPhone.trim() || null,
      supportWhatsapp: form.supportWhatsapp.trim() || null,
      legalEmail: form.legalEmail.trim() || null,
      officeAddress: form.officeAddress.trim() || null,
    };
    update.mutate(payload, {
      onSuccess: () => toast.success("Support & contact saved"),
      onError: (e) => toast.error(e.message),
    });
  };

  if (isLoading) return <Skeleton className="h-44 w-full" />;

  return (
    <section className="rounded-card border border-border p-5 bg-card space-y-4">
      <header>
        <h2 className="text-lg font-semibold">Support & contact</h2>
        <p className="text-sm text-muted-foreground">
          Displayed in the customer and provider apps (help screens, booking
          emails, invoice footer).
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Support email">
          <Input
            type="email"
            placeholder="support@example.com"
            value={form.supportEmail}
            onChange={(e) => setForm({ ...form, supportEmail: e.target.value })}
          />
        </Field>
        <Field label="Support phone">
          <Input
            placeholder="+91…"
            value={form.supportPhone}
            onChange={(e) => setForm({ ...form, supportPhone: e.target.value })}
          />
        </Field>
        <Field label="Support WhatsApp">
          <Input
            placeholder="+91…"
            value={form.supportWhatsapp}
            onChange={(e) =>
              setForm({ ...form, supportWhatsapp: e.target.value })
            }
          />
        </Field>
        <Field label="Legal / DPO email">
          <Input
            type="email"
            placeholder="legal@example.com"
            value={form.legalEmail}
            onChange={(e) => setForm({ ...form, legalEmail: e.target.value })}
          />
        </Field>
        <div className="md:col-span-2">
          <Field label="Office address">
            <Textarea
              rows={2}
              value={form.officeAddress}
              onChange={(e) =>
                setForm({ ...form, officeAddress: e.target.value })
              }
            />
          </Field>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={update.isPending}>
          {update.isPending ? "Saving…" : "Save contact info"}
        </Button>
      </div>
    </section>
  );
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-1">
    <Label>{label}</Label>
    {children}
  </div>
);

export default SupportContactSection;
